// The kit of the ☸ OPTIONS menu: pure markup builders for the look of
// card 20 of mockup round 081 (ui-map-interface.md §13, R1731).
//
// Two hosts draw with it: the in-game menu of `ui/overlay.ts` and the hub
// SETTINGS panel. A builder returns a string and reads no state; every
// control carries the `data-act` attributes its caller names, so each
// host routes the presses through its own dispatcher (the overlay's
// `actButton`, the hub's `Panel.act`).
//
// Rules every builder keeps:
//
//   - A text parameter is plain text, and the builder escapes it. A
//     parameter named `control`, `body` or `html` is markup the caller
//     built, and it goes in as it stands.
//   - A state reads by shape (rule 3 of the vision): a switch moves its
//     knob and writes ON or OFF. The choices come from the choice kit
//     (`ui/choice/choice.ts`, ui-map-interface.md §18, R2011), which this
//     file re-exports for its present callers.
//   - The value of a volume range is never in the markup. A value in the
//     string would change the markup on every tick of a drag, and the host
//     would rebuild the range under the finger. `rows.ts` `syncRangeMarks`
//     writes the values after each rebuild.
//   - The scope mark stands on the two exceptions alone: DEVICE and
//     EMPIRE. A row with no mark is kept against the account (card 15).
//
// `options.css` holds the look; every rule stands under the root class
// `KIT_ROOT`, and a host gives its root one of the two shell classes.

import { options as S, overlay as OS, squad as SQ } from '../../gen/strings';
import { esc } from '../esc';
import { textGlyph } from '../glyph';
import {
  type Press, button, labelAttr, pressAttrs, tag, titleAttr,
} from '../choice/choice';
import { clamp01 } from '../../render/math';
import { lineIcon } from '../lineicon';

export { type Choice, type Confirm, type Press, pressAttrs, stepper, tag } from '../choice/choice';

/** The root class of the kit. `options.css` scopes every rule under it. */
export const KIT_ROOT = 'opt';
/** The shell class of a desktop root: a centred popup with a rail. */
export const SHELL_DESK = 'desk';
/** The shell class of a telephone root: the whole screen, a page at a time. */
export const SHELL_PHONE = 'phone';
/** The shell class of the menu beside the landscape rail: a rail and a
 *  pane right of the rail (round 090 card 11 B, R1848). It wears
 *  `SHELL_DESK` too, so the rows take the desktop form. */
export const SHELL_SIDE = 'side';

// --- controls ---------------------------------------------------------------

/**
 * The slide switch (card 11 b): a knob and the word ON or OFF inside it,
 * so the state reads by the place of the knob. It is a `role="switch"`
 * button, 44 px tall on a coarse pointer.
 *
 * Two states take no press. `disabled` is a pause (a call in flight, a
 * read that travels): the switch keeps its face on a dashed rim. An
 * `unavailable` switch cannot work on this device or host at all (push
 * that the browser or the server cannot give): it draws no knob and the
 * short word N/A in place of ON or OFF, so it never reads as OFF (R1762).
 * The row's own sentence says why.
 */
export function switchCtl(on: boolean, press: Press,
  o: { label?: string; disabled?: boolean; unavailable?: boolean } = {}): string {
  const label = labelAttr(o.label);
  if (o.unavailable) {
    return `<button type="button" class="o-sw na" role="switch" aria-checked="false" aria-disabled="true"`
      + `${pressAttrs(press)}${label} title="${esc(S.word_na_title)}" disabled>`
      + `<span class="o-sww">${S.word_na}</span></button>`;
  }
  return `<button type="button" class="o-sw${on ? ' on' : ''}" role="switch" aria-checked="${on}"`
    + `${pressAttrs(press)}${label}${o.disabled ? ' disabled' : ''}>`
    + switchFace(on) + '</button>';
}

/** The inside of a switch: the knob and the word. The quick tile draws
 *  the same face inside its own button. */
function switchFace(on: boolean): string {
  return `<span class="o-knob"></span><span class="o-sww">${on ? S.word_on : S.word_off}</span>`;
}

/** The two volume keys of the settings blob. */
export type VolumeKey = 'musicVol' | 'sfxVol';

/** The keys of the blob a range control writes: the two volumes, the
 *  idle time of the sea view (R2865) and the two mark scales (R3246). */
export type RangeKey = VolumeKey | 'seaIdleMin' | 'markScaleArcade' | 'markScaleCore';

/**
 * The range control (R2865): the range, then the figure. The range and
 * the figure carry no value: the host writes both after the markup stands
 * (`rows.ts` `syncRangeMarks`), so a rebuild never moves a range under a
 * finger. `wide` gives the figure room for a word after the number.
 */
export function rangeCtl(o: {
  key: RangeKey; min: number; max: number; step: number; label: string; wide?: boolean;
}): string {
  return `<input class="o-range" type="range" min="${o.min}" max="${o.max}" step="${o.step}" data-key="${o.key}"`
    + ` aria-label="${esc(o.label)}">`
    + `<span class="o-fig${o.wide ? ' wide' : ''}" data-vol="${o.key}">—</span>`;
}

/**
 * The volume control (card 12): the mute glyph IS the switch, then the
 * range of whole percent in steps of five, then the figure.
 */
export function volumeCtl(o: {
  key: VolumeKey; on: boolean; name: string; mute: Press;
}): string {
  const glyph = o.on ? `${textGlyph('◖')})` : `${textGlyph('◖')}×`;
  const title = o.on ? S.mute_title({ name: o.name }) : S.unmute_title({ name: o.name });
  return `<span class="o-vol${o.on ? '' : ' off'}">`
    + `<button type="button" class="o-mute" aria-pressed="${!o.on}"${pressAttrs(o.mute)}`
    + `${titleAttr(title)} aria-label="${esc(title)}">${glyph}</button>`
    + rangeCtl({ key: o.key, min: 0, max: 100, step: 5, label: S.vol_label({ name: o.name }) })
    + '</span>';
}

/** A gain as the whole percent a range, a figure and a summary show. */
export function percentOf(gain: number): number {
  return Math.round(clamp01(gain) * 100);
}

// --- rows -------------------------------------------------------------------

/** The two scopes that carry a mark. ACCOUNT is the default and has none. */
export type Scope = 'device' | 'empire';

/** The scope mark of a row (card 15). */
export function scopeMark(scope: Scope): string {
  return scope === 'device'
    ? `<em class="o-scope dev" title="${esc(S.scope_device_title)}">${S.scope_device}</em>`
    : `<em class="o-scope emp" title="${esc(S.scope_empire_title)}">${S.scope_empire}</em>`;
}

/** One settings row. */
export interface RowSpec {
  name: string;
  /** The sentence under the name. */
  what?: string;
  /** The control, as markup. */
  control: string;
  /** The scope mark, on an exception alone. */
  scope?: Scope;
  /** The value differs from `DEFAULT_SETTINGS`: the changed dot. */
  changed?: boolean;
  /** The control takes a line of its own under the name. */
  wrap?: boolean;
  /** A mark after the name, as markup (the WEEKEND tag). */
  mark?: string;
  /** More markup under the row, inside it (a long sentence). */
  more?: string;
}

/** The settings row: the name, the sentence, the control, an optional
 *  scope mark and the changed dot. */
export function row(r: RowSpec): string {
  return `<div class="o-row${r.wrap ? ' wrap' : ''}${r.more ? ' more' : ''}${r.changed ? ' chg' : ''}">`
    + (r.changed ? `<span class="o-chg" title="${esc(S.changed_title)}"></span>` : '')
    + `<span class="o-k"><b>${esc(r.name)}${r.scope ? ` ${scopeMark(r.scope)}` : ''}`
    + `${r.mark ?? ''}</b>${r.what ? `<i>${esc(r.what)}</i>` : ''}</span>`
    + `<span class="o-c">${r.control}</span>${r.more ?? ''}</div>`;
}

/** A row whose control is one action button (OPEN, KEYS, PHOTO). */
export function actionRow(name: string, what: string, label: string, press: Press): string {
  return row({ name, what, control: button(label, press, { variant: 'primary' }) });
}

/**
 * One link row (card 18): the whole row is the press, and ↗ says that it
 * leaves for the hub. `bad` paints the name red (the account delete).
 */
export function linkRow(name: string, what: string, press: Press, bad = false): string {
  return `<button type="button" class="o-link${bad ? ' bad' : ''}"${pressAttrs(press)}>`
    + `<span class="o-k"><b>${esc(name)}</b><i>${esc(what)}</i></span>`
    + `<span class="o-go">${textGlyph('↗')}</span></button>`;
}

/** The rows of the hub links, two columns on a desktop. */
export function linkList(rows: readonly string[]): string {
  return `<div class="o-links">${rows.join('')}</div>`;
}

/** The long sentence a row may hold on a line of its own (`RowSpec.more`). */
export function longLine(text: string): string {
  return `<p class="o-long">${esc(text)}</p>`;
}

/** A boxed line that asks for an act, with its control under it (the
 *  guest seat and REGISTER). */
export function callout(text: string, control: string): string {
  return `<div class="o-call"><span>${esc(text)}</span>${control}</div>`;
}

/**
 * LOG OUT at the foot of the ACCOUNT page (R1731): red at all times, and a
 * two-press control (`arm` in its press). `armed` paints the armed state,
 * and `what` is the sentence beside it: what the press ends, or, armed,
 * what stays and what does not. `armLabel` is the armed face that names
 * the act; it shares one cell with `label`, so the press keeps its width
 * (R2069).
 */
export function logoutBlock(o: { label: string; armLabel?: string; what: string; armed: boolean; press: Press }): string {
  return `<div class="o-out${o.armed ? ' armed' : ''}"><span>${esc(o.what)}</span>`
    + button(o.label, o.press, { variant: 'danger', armFace: o.armLabel, armed: o.armed }) + '</div>';
}

/** A group heading inside a page, with a quiet aside. `heading` writes it
 *  as an `h3`, the level under the `h2` page title of the hub outline
 *  (R2048); the game menu keeps the `div`, because its page title is no
 *  heading. `options.css` draws the `h3` as the `div`. */
export function groupHead(name: string, aside = '', mark = '', heading = false): string {
  const tag = heading ? 'h3' : 'div';
  return `<${tag} class="o-sec"><b>${esc(name)}</b>${mark}${aside ? `<i>${esc(aside)}</i>` : ''}</${tag}>`;
}

/** A quiet line of prose. `warn` paints it amber. */
export function note(text: string, warn = false): string {
  return `<div class="o-note${warn ? ' warn' : ''}">${esc(text)}</div>`;
}

/** The title of a page: the glyph in its box, the word, the sentence.
 *  `big` is the telephone title, which stands in the body. An empty `what`
 *  draws no line under the word (a hub panel whose summary is not known
 *  yet, R1921). `heading` writes the word as an `h2`, the level of a page
 *  title in the hub outline (R2048); the game menu keeps the span. */
export function pageTitle(o: { glyph: string; name: string; what: string; big?: boolean; mark?: string; heading?: boolean }): string {
  const tag = o.heading ? 'h2' : 'span';
  return `<div class="o-pt${o.big ? ' big' : ''}"><span class="o-ptg" aria-hidden="true">${o.glyph}</span>`
    + `<${tag} class="o-ptn">${esc(o.name)}</${tag}>${o.mark ?? ''}</div>`
    + (o.what === '' ? '' : `<div class="o-pw">${esc(o.what)}</div>`);
}

/** The legend at the foot of the desktop pane (card 20). `scopes` names
 *  the scope marks the rows of the menu carry; a menu whose rows carry no
 *  scope mark, the arcade match menu, explains none (R2060). */
export function legend(scopes: readonly Scope[] = ['device', 'empire']): string {
  return `<span class="o-lg"><span class="o-dot"></span>${esc(S.legend_changed)}</span>`
    + `<span class="o-lg"><span class="o-nomark">${esc(S.legend_nomark)}</span>${esc(S.legend_account)}</span>`
    + (scopes.includes('device') ? `<span class="o-lg">${scopeMark('device')}${esc(S.legend_device)}</span>` : '')
    + (scopes.includes('empire') ? `<span class="o-lg">${scopeMark('empire')}${esc(S.legend_empire)}</span>` : '');
}

// --- the frame --------------------------------------------------------------

/** One section as the rail and the tiles draw it. */
export interface NavItem {
  glyph: string;
  name: string;
  /** The summary line, or '' for none. */
  sum: string;
  press: Press;
  /** The count the row and the tile carry (applications, unread lines,
   *  new grades); none at 0 or undefined (ui-map-interface.md §16.16). */
  badge?: number;
  /** The tile spans both columns of the tiles. */
  wide?: boolean;
  /** One more class on the rail row (the section shell's `hs-compact`). */
  cls?: string;
}

/** The count of a rail row or a tile, or '' for none. */
function navBadge(it: NavItem): string {
  return it.badge !== undefined && it.badge > 0 ? `<span class="o-bd">${it.badge}</span>` : '';
}

/** One rail row of the desktop popup: the glyph, the word and the faint
 *  summary, and the count where the section has one. The open section
 *  wears the accent bar. The glyph of a rail row and of a section tile is
 *  hidden from a screen reader, so the name of the press starts with the
 *  word (R2153). */
export function railItem(it: NavItem, on: boolean): string {
  return `<button type="button" class="o-it${on ? ' on' : ''}${it.cls ? ` ${it.cls}` : ''}"`
    + `${on ? ' aria-current="page"' : ''}${pressAttrs(it.press)}>`
    + `<span class="g" aria-hidden="true">${it.glyph}</span><span class="t"><b>${esc(it.name)}</b>`
    + `${it.sum ? `<i>${esc(it.sum)}</i>` : ''}</span>${navBadge(it)}</button>`;
}

/** One section tile of the telephone root. `wide` spans both columns. */
function sectionTile(it: NavItem, wide = false): string {
  return `<button type="button" class="o-st${wide ? ' wide' : ''}${it.sum ? '' : ' nosum'}"${pressAttrs(it.press)}>`
    + `<span class="o-stg" aria-hidden="true">${it.glyph}</span><span class="o-stt"><b>${esc(it.name)}</b>`
    + `${it.sum ? `<i>${esc(it.sum)}</i>` : ''}</span>${navBadge(it)}<span class="o-chev" aria-hidden="true">›</span></button>`;
}

/**
 * The grid of section tiles, two columns. A tile that asks for it (`wide`)
 * spans both, and so does a tile that would stand alone in its row: the
 * last tile of an odd list, or the tile before a wide one.
 */
export function sectionTiles(items: readonly NavItem[]): string {
  let left = true;
  return '<div class="o-sg">' + items.map((it, i) => {
    const next = items[i + 1];
    const span = it.wide === true || (left && (next === undefined || next.wide === true));
    left = span || !left;
    return sectionTile(it, span);
  }).join('') + '</div>';
}

/**
 * One tile of the QUICK row (card 05, R1731): a large switch. A volume
 * tile shows its figure and a level bar, which the host fills
 * (`syncRangeMarks`), so its markup holds no level. `narrow` is the
 * tile of a row of four, which shows a tag for the state (R2011).
 */
export function quickTile(o: {
  glyph: string; name: string; on: boolean; press: Press; vol?: VolumeKey; narrow?: boolean;
}): string {
  let state: string;
  if (o.vol) state = `<span class="o-qfig" data-vol="${o.vol}">—</span>`;
  else if (o.narrow) state = tag(o.on ? S.word_on : S.word_off, o.on ? 'accent' : 'plain');
  else state = `<span class="o-sw xs${o.on ? ' on' : ''}">${switchFace(o.on)}</span>`;
  return `<button type="button" class="o-qt${o.on ? ' on' : ''}${o.narrow ? ' narrow' : ''}" aria-pressed="${o.on}"`
    + `${pressAttrs(o.press)}><span class="o-qtop"><span class="g">${o.glyph}</span>${state}</span>`
    + `<b>${esc(o.name)}</b>`
    + (o.vol ? `<span class="o-qbar"><i data-volbar="${o.vol}"></i></span>` : '') + '</button>';
}

/** The row of quick tiles: two by two, or one row of four. */
export function quickRow(tiles: string[], four: boolean): string {
  return `<div class="o-qg${four ? ' four' : ''}">${tiles.join('')}</div>`;
}

/** A group label of the telephone root: QUICK, SECTIONS, SESSION. */
export function rootLabel(text: string): string {
  return `<div class="o-lab">${esc(text)}</div>`;
}

/** ‹ back to the page above: OPTIONS from a section, AUDIO from the
 *  music player. */
export function backButton(label: string, press: Press): string {
  return `<button type="button" class="o-back"${pressAttrs(press)}>‹ ${esc(label)}</button>`;
}

/**
 * The head of the desktop popup: the title, the world line (the world and
 * the day, `head_line`) and the ESC mark beside the 40 px ✕. The menu
 * beside the landscape rail puts `tail` (MENU and ARCADE) in the place of
 * ✕, because the MENU cap of the rail closes it (round 090 card 11 B).
 */
export function head(o: {
  title?: string; glyph?: string; world?: string; esc?: boolean; close?: Press; tail?: string;
}): string {
  const close = o.close ? `<button type="button" class="o-x"${pressAttrs(o.close)}`
    + ` title="${esc(S.close_title)}" aria-label="${esc(S.close_title)}">✕</button>` : '';
  return '<div class="o-head">'
    + (o.title ? `<span class="o-title">${o.glyph ? `<span class="g">${o.glyph}</span>` : ''}${esc(o.title)}</span>` : '')
    + (o.world ? `<span class="o-world">${esc(o.world)}</span>` : '')
    + '<span class="o-sp"></span>'
    + (o.esc ? `<span class="o-esc">${esc(S.esc)}</span>` : '')
    + (o.tail ?? close) + '</div>';
}

/** One step of the path in the head of the telephone menu. */
export interface Crumb {
  /** The glyph, as markup (`textGlyph`), or '' for none. */
  glyph: string;
  name: string;
}

/**
 * The head of the telephone menu (card 02 of mockup round 082, R1754): the
 * title alone, and no control, because the caps of the tab bar close the
 * menu and go back. The root names ☸ OPTIONS and the world line. A page
 * names its path, `OPTIONS › AUDIO`, so the head names the screen that the
 * ‹ BACK cap opens. The last step wears the accent and the steps before it
 * are faint. `mark` stands after the last step (the DEVICE mark of ALERTS).
 */
export function crumbHead(path: readonly Crumb[], o: { world?: string; mark?: string } = {}): string {
  const steps = path.map((c, i) => {
    const g = c.glyph ? `<span class="g">${c.glyph}</span>` : '';
    return (i > 0 ? '<span class="o-crs" aria-hidden="true">›</span>' : '')
      + `<span class="${i === path.length - 1 ? 'o-title' : 'o-cr'}">${g}${esc(c.name)}</span>`;
  }).join('');
  return `<div class="o-ch">${steps}${o.mark ?? ''}`
    + `${o.world ? `<span class="o-world">${esc(o.world)}</span>` : ''}</div>`;
}

/** The commander as the session block, the ACCOUNT page and the hub
 *  SETTINGS panel show it. */
export interface Commander {
  name: string;
  /** True for a guest account, false for a registered one, and undefined
   *  where the page knows no account (no meta server). */
  guest?: boolean;
  /** The quiet line after the kind mark: the faction in the hub, or ''.
   *  The game menu draws none: its head names the world and the day
   *  (R1731). */
  line: string;
}

/** The kind mark of an account: REGISTERED or GUEST. */
function kindMark(guest: boolean | undefined): string {
  if (guest === undefined) return '';
  return guest
    ? `<em class="o-kind guest">${S.kind_guest}</em>`
    : `<em class="o-kind">${S.kind_registered}</em>`;
}

/** The first letter of a name, for the round mark. */
function initial(name: string): string {
  const m = /[\p{L}\p{N}]/u.exec(name);
  return m ? m[0].toUpperCase() : '?';
}

/** The round mark of a commander with no picture: the first letter, or
 *  `?` for a guest. */
function roundMark(c: Commander): string {
  return `<span class="av">${esc(c.guest ? '?' : initial(c.name))}</span>`;
}

/** The face of a commander, the one builder of the commander row and the
 *  commander card (R1766): the faction badge where the page knows the
 *  faction, as `<img>` markup (`ui/chart/faction-key.ts` `badgePicture`),
 *  else the round mark. */
function commanderFace(c: Commander, badge?: string): string {
  return badge ? `<span class="o-fb">${badge}</span>` : roundMark(c);
}

/**
 * The commander card of the ACCOUNT page and of the hub SETTINGS panel:
 * the face (the faction badge, or the round mark with no `badge`), the
 * name and the kind mark. With a `press` it is the button that opens the
 * ACCOUNT page (`on` while that page stands).
 */
export function commanderCard(c: Commander,
  o: { press?: Press; on?: boolean; big?: boolean; badge?: string } = {}): string {
  const inner = commanderFace(c, o.badge) + `<span class="t"><b>${esc(c.name)}</b><i>${kindMark(c.guest)}`
    + `${c.line ? `<span>${esc(c.line)}</span>` : ''}</i></span>`;
  const cls = `o-who${o.on ? ' on' : ''}${o.big ? ' big' : ''}`;
  if (!o.press) return `<div class="${cls}">${inner}</div>`;
  return `<button type="button" class="${cls}"${pressAttrs(o.press)} title="${esc(S.who_title)}">`
    + `${inner}<span class="o-chev">›</span></button>`;
}

/** The rank cell in words (R2176): the Honour rank and the place in the
 *  season table, which `Drifter #8` states in short. */
function rankWords(m: { title: string; rank: number }): string {
  return m.rank > 0 ? OS.bar_rank_label({ title: m.title, rank: m.rank }) : OS.bar_rank_label_new({ title: m.title });
}

/** The line icons of the session block, in a 24-unit box: the two session
 *  buttons (R1740), and the rank and the standing orders of the commander
 *  row (card 01 of mockup round 082, R1757). */
const SESSION_ICON = {
  leave: '<path d="M13.5 4H5.5v16h8"/><path d="M10 12h10.5M17 8.5l3.5 3.5-3.5 3.5"/>',
  arcade: '<circle cx="12" cy="12" r="7.5"/><path d="M12 2.5v5M12 16.5v5M2.5 12h5M16.5 12h5"/>'
    + '<circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>',
  rank: '<path d="M6 13l6-5 6 5M6 18.5l6-5 6 5"/>',
  orders: '<rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 4V2.8h6V4M8.5 10h7M8.5 14h7M8.5 18h4"/>',
} as const;

/** One line icon of the session block, `px` square, hidden from a screen
 *  reader. */
function sessionIcon(name: keyof typeof SESSION_ICON, px: number): string {
  return lineIcon('o-ic', px, SESSION_ICON[name]);
}

/** A session button: its line icon, then its word, in the dark ink. The
 *  word stands in a span of its own, so a narrow block ends it in an
 *  ellipsis. */
function sessionButton(label: string, icon: keyof typeof SESSION_ICON, press: Press, cls: string, title: string): string {
  return button('', press, { cls: `dark ${cls}`, title, html: `${sessionIcon(icon, 16)}<span>${esc(label)}</span>` });
}

/** The squad cell of the commander row. */
export interface SquadCell {
  /** The squad mark: the reader's squad hull as the chip draws it, as
   *  `<img>` markup (`ui/squadmarks.ts`), or '' with no picture. */
  icon: string;
  /** The clan tag; '' for a pickup squad, which names no clan. */
  tag: string;
  /** The squad number, 1 to 5. */
  n: number;
  /** The members of the squad, the reader included. */
  count: number;
  /** A squad of an NPC clan (player-reach.md §5.2): PICKUP on a dashed rim. */
  pickup: boolean;
  /** The cup mark of the clan, as markup (`ui/cupmark.ts`), or ''. */
  cup: string;
}

/** What the commander row shows beside the name (R1757). */
export interface CommanderMarks {
  /** The badge of the reader's faction, as `<img>` markup (the badge of
   *  the faction key, `ui/chart/faction-key.ts` `badgePicture`), or ''
   *  where the page draws none: the round mark stands in its place. */
  badge: string;
  /** The autopilot keeps the empire under standing orders (R1256). */
  orders: boolean;
  /** The reader's squad, or null with no squad. */
  squad: SquadCell | null;
  /** The Honour title of the seat, or '' before the hello names one. */
  title: string;
  /** The place on the season ladder, or 0 before the first board. */
  rank: number;
}

/**
 * The commander row of the session block (R1757; card 01 of mockup round
 * 082, the owner's order of 2026-10-07): one row of three cells on one
 * centre line, divided by hairlines, each a press of its own.
 *
 *   - The commander: the faction badge, the name, the GUEST mark of a
 *     guest, the standing-orders mark while the orders run, and the
 *     chevron. It opens the ACCOUNT page (`me`), and `on` lights it while
 *     that page stands.
 *   - The squad: its mark, the clan tag, the squad number and its head
 *     count, and the cup mark. It opens the roster card (`squad`).
 *   - The rank: its icon, the Honour title and the place. It opens
 *     STANDINGS (`rank`).
 *
 * A cell with nothing to show is not drawn, and with no `marks` the row
 * holds the commander alone. The words a short screen drops carry a class
 * of their own (`o-mt` the title, `o-mn` the head count), so the
 * stylesheet drops a word before it drops a cell.
 */
export function commanderBar(who: Commander, m: CommanderMarks | null, o: {
  me: Press; squad: Press; rank: Press; on?: boolean;
  /** The desktop foot: the commander cell alone, with the kind line
   *  (GUEST or REGISTERED) under the name, and no squad or rank cell. */
  solo?: boolean;
}): string {
  const face = commanderFace(who, m?.badge);
  const orders = m?.orders
    ? `<span class="o-so" role="img" title="${esc(OS.bar_standing_title)}" aria-label="${esc(OS.bar_standing)}">`
      + `${sessionIcon('orders', 12)}</span>` : '';
  const me = `<button type="button" class="o-mc o-mh${o.on ? ' on' : ''}"${o.on ? ' aria-current="page"' : ''}`
    + `${pressAttrs(o.me)} title="${esc(S.who_title)}">${face}`
    + (o.solo ? `<span class="o-nt"><span class="o-nm">${esc(who.name)}</span>${kindMark(who.guest)}</span>`
      : `<span class="o-nm">${esc(who.name)}</span>${who.guest ? kindMark(true) : ''}`)
    + `${orders}<span class="o-chev">›</span></button>`;
  if (o.solo) return `<div class="o-me solo">${me}</div>`;
  const sq = m?.squad;
  const squad = !sq ? ''
    : `<button type="button" class="o-mc o-msq${sq.pickup ? ' pick' : ''}"${pressAttrs(o.squad)}`
      + ` title="${esc(SQ.chip_title)}">${sq.icon}<span class="o-mw">`
      + (sq.pickup ? esc(S.me_pickup) : `<b>${esc(sq.tag)}</b> ${sq.n}`)
      + `<span class="o-mn">·${sq.count}</span></span>${sq.cup}</button>`;
  const rank = !m || m.title === '' ? ''
    : `<button type="button" class="o-mc o-mrk"${pressAttrs(o.rank)} title="${esc(rankWords(m))}">`
      + `${sessionIcon('rank', 13)}<span class="o-mw"><span class="o-mt">${esc(m.title)}</span>`
      + `${m.rank > 0 ? ` <b>#${m.rank}</b>` : ''}</span></button>`;
  return `<div class="o-me">${me}${squad}${rank}</div>`;
}

/**
 * The session block (R1731, R1757): the commander row of three cells,
 * then LEAVE TO THE HUB as the large button and ARCADE beside it in one
 * row, both in the dark ink of card 02 of round 082 (R1740). It keeps its
 * two rows on every screen. `compact` is the block of a short telephone
 * root: the commander row drops words and the buttons take 44 px.
 */
export function sessionBlock(o: {
  who: Commander; marks: CommanderMarks | null; me: Press; squad: Press; rank: Press;
  menu: Press; arcade: Press; compact?: boolean; on?: boolean;
  /** The desktop foot: one row, the commander at the left and the two
   *  buttons at the right (card 01 of round 082). */
  solo?: boolean;
}): string {
  return `<div class="o-sess${o.compact ? ' compact' : ''}${o.solo ? ' solo' : ''}">`
    + commanderBar(o.who, o.marks, { me: o.me, squad: o.squad, rank: o.rank, on: o.on, solo: o.solo })
    + sessionButtons(o.menu, o.arcade) + '</div>';
}

/** LEAVE TO THE HUB and ARCADE of the session block, alone: the head of
 *  the menu beside the landscape rail holds them (round 090 card 11 B). */
export function sessionButtons(menu: Press, arcade: Press): string {
  return '<div class="o-sessb">'
    + sessionButton(S.leave_hub, 'leave', menu, 'o-menu', S.leave_hub_title)
    + sessionButton(S.arcade, 'arcade', arcade, 'o-arcade', S.arcade_title) + '</div>';
}
